import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RecyclingPoint } from '../../types/recycling-point.interface';

/**
 * Points Cache Service
 * IndexedDB-backed cache for recycling points.
 *
 * Prevents data loss when the user taps "Buscar acá" by persisting
 * previously-fetched points across viewport changes and page refreshes.
 *
 * - TTL: 30 minutes (expired entries are purged on read)
 * - Max entries: 3 000 (oldest evicted on overflow)
 * - Merge: fresh API points first, then remaining cached (deduped by id)
 */
@Injectable({ providedIn: 'root' })
export class PointsCacheService {
  private static readonly DB_NAME = 'remateriar-cache';
  private static readonly STORE_NAME = 'points';
  private static readonly DB_VERSION = 1;
  private static readonly TTL_MS = 30 * 60 * 1000; // 30 min
  private static readonly MAX_ENTRIES = 3_000;

  private readonly platformId = inject(PLATFORM_ID);
  private dbPromise: Promise<IDBDatabase> | null = null;

  // ── helpers ──────────────────────────────────────────────────────

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Lazy-open (or return cached) IDB database.
   * Creates the `points` object store on first run.
   */
  private getDb(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(
        PointsCacheService.DB_NAME,
        PointsCacheService.DB_VERSION,
      );

      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(PointsCacheService.STORE_NAME)) {
          db.createObjectStore(PointsCacheService.STORE_NAME, { keyPath: 'id' });
        }
      };

      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        this.dbPromise = null; // allow retry on next call
        reject(req.error);
      };
    });

    return this.dbPromise;
  }

  // ── public API ───────────────────────────────────────────────────

  /**
   * Upsert points into cache.  Sets `_cachedAt = Date.now()` on every entry.
   * After writing, evicts the oldest entries if total > MAX_ENTRIES.
   */
  async upsertMany(points: RecyclingPoint[]): Promise<void> {
    if (!this.isBrowser || points.length === 0) return;

    try {
      const db = await this.getDb();
      const now = Date.now();

      // Write all points in a single transaction
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(PointsCacheService.STORE_NAME, 'readwrite');
        const store = tx.objectStore(PointsCacheService.STORE_NAME);

        for (const point of points) {
          store.put({ ...point, _cachedAt: now });
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });

      // Evict overflow (separate transaction so the write is committed first)
      await this.evictOverflow(db);
    } catch (err) {
      console.error('[PointsCache] upsertMany failed:', err);
    }
  }

  /**
   * Return all non-expired cached points (without the `_cachedAt` metadata).
   * Expired entries are deleted lazily in the background.
   */
  async getAll(): Promise<RecyclingPoint[]> {
    if (!this.isBrowser) return [];

    try {
      const db = await this.getDb();
      const all = await this.readAll(db);
      const now = Date.now();
      const ttl = PointsCacheService.TTL_MS;

      const valid: RecyclingPoint[] = [];
      const expiredKeys: string[] = [];

      for (const entry of all) {
        if (now - (entry._cachedAt ?? 0) > ttl) {
          expiredKeys.push(entry.id);
        } else {
          // Strip cache metadata before returning
          const { _cachedAt, ...point } = entry;
          valid.push(point as RecyclingPoint);
        }
      }

      // Fire-and-forget purge of expired entries
      if (expiredKeys.length > 0) {
        this.deleteKeys(db, expiredKeys).catch(() => {});
      }

      return valid;
    } catch (err) {
      console.error('[PointsCache] getAll failed:', err);
      return [];
    }
  }

  /**
   * Explicitly purge all expired entries. Returns the count deleted.
   */
  async purgeExpired(): Promise<number> {
    if (!this.isBrowser) return 0;

    try {
      const db = await this.getDb();
      const all = await this.readAll(db);
      const now = Date.now();
      const ttl = PointsCacheService.TTL_MS;

      const expiredKeys = all
        .filter(e => now - (e._cachedAt ?? 0) > ttl)
        .map(e => e.id);

      if (expiredKeys.length > 0) {
        await this.deleteKeys(db, expiredKeys);
      }

      return expiredKeys.length;
    } catch (err) {
      console.error('[PointsCache] purgeExpired failed:', err);
      return 0;
    }
  }

  /**
   * Merge fresh API points with cached points.
   *
   * Result order:
   *  1. `fresh` (already sorted by distance from backend)
   *  2. remaining `cached` that are NOT in `fresh` (deduped by `id`)
   */
  merge(fresh: RecyclingPoint[], cached: RecyclingPoint[]): RecyclingPoint[] {
    const freshIds = new Set(fresh.map(p => p.id));
    const rest = cached.filter(p => !freshIds.has(p.id));
    return [...fresh, ...rest];
  }

  /**
   * Delete every entry from the cache.
   */
  async clear(): Promise<void> {
    if (!this.isBrowser) return;

    try {
      const db = await this.getDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(PointsCacheService.STORE_NAME, 'readwrite');
        tx.objectStore(PointsCacheService.STORE_NAME).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (err) {
      console.error('[PointsCache] clear failed:', err);
    }
  }

  // ── private helpers ──────────────────────────────────────────────

  /** Read every row from the store. */
  private readAll(db: IDBDatabase): Promise<(RecyclingPoint & { _cachedAt: number })[]> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PointsCacheService.STORE_NAME, 'readonly');
      const req = tx.objectStore(PointsCacheService.STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  /** Delete a batch of keys in a single transaction. */
  private deleteKeys(db: IDBDatabase, keys: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PointsCacheService.STORE_NAME, 'readwrite');
      const store = tx.objectStore(PointsCacheService.STORE_NAME);
      for (const key of keys) {
        store.delete(key);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * If the total entry count exceeds MAX_ENTRIES, delete the oldest
   * (by `_cachedAt`) until we're back within the limit.
   */
  private async evictOverflow(db: IDBDatabase): Promise<void> {
    const all = await this.readAll(db);

    if (all.length <= PointsCacheService.MAX_ENTRIES) return;

    // Sort ascending by _cachedAt (oldest first)
    all.sort((a, b) => (a._cachedAt ?? 0) - (b._cachedAt ?? 0));

    const excess = all.length - PointsCacheService.MAX_ENTRIES;
    const keysToDelete = all.slice(0, excess).map(e => e.id);

    await this.deleteKeys(db, keysToDelete);
  }
}
